import React from "react";

import { Forms, emptyContent, SubscribersSelector } from "turboui";
import * as Goals from "@/models/goals";

import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import { SubscriptionsState, useSubscriptionsAdapter } from "@/models/subscriptions";
import { pageCacheKey as goalPageCacheKey } from "@/pages/GoalPage";
import { PageCache } from "@/routes/PageCache";
import { useNavigateTo } from "@/routes/useNavigateTo";
import { assertPresent } from "@/utils/assertions";
import { useLoadedData } from "./loader";

import { usePaths } from "@/routes/paths";

export function Form() {
  const paths = usePaths();
  const { goal } = useLoadedData();

  const [close] = Goals.useCloseGoal();
  const navigateToGoal = useNavigateTo(paths.goalPath(goal.id!));

  assertPresent(goal.potentialSubscribers, "potentialSubscribers must be present in goal");
  assertPresent(goal.space, "space must be present in goal");

  const subscriptionsState = useSubscriptionsAdapter(goal.potentialSubscribers, {
    ignoreMe: true,
    notifyPrioritySubscribers: true,
    spaceName: goal.space.name,
  });

  const form = Forms.useForm({
    fields: {
      success: "yes",
      retrospective: emptyContent(),
    },
    submit: async () => {
      const successStatus = form.values.success === "yes" ? "achieved" : "missed";

      await close({
        goalId: goal.id,
        success: form.values.success,
        successStatus: successStatus,
        retrospective: JSON.stringify(form.values.retrospective),
        sendNotificationsToEveryone: subscriptionsState.notifyEveryone,
        subscriberIds: subscriptionsState.currentSubscribersList,
      });
      PageCache.invalidate(goalPageCacheKey(goal.id));
      navigateToGoal();
    },
    cancel: navigateToGoal,
  });

  return (
    <Forms.Form form={form}>
      <Forms.FieldGroup>
        <AccomplishedOrDropped />
        <RetrospectiveNotes />
      </Forms.FieldGroup>

      <Subscribers subscriptionsState={subscriptionsState} />

      <Forms.Submit saveText="Закрыть цель" />
    </Forms.Form>
  );
}

function AccomplishedOrDropped() {
  return (
    <Forms.RadioButtons
      field="success"
      label="Была ли достигнута эта цель?"
      options={[
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ]}
    />
  );
}

function RetrospectiveNotes() {
  const { goal } = useLoadedData();
  const richTextHandlers = useRichEditorHandlers({ scope: { type: "goal", id: goal.id! } });

  return (
    <Forms.RichTextArea
      field="retrospective"
      label="Заметки обзора итогов"
      richTextHandlers={richTextHandlers}
      placeholder="Что получилось? Что не получилось? Чему ты научился(ась)?"
      required
    />
  );
}

function Subscribers({ subscriptionsState }: { subscriptionsState: SubscriptionsState }) {
  return (
    <div className="my-10">
      <SubscribersSelector {...subscriptionsState} />
    </div>
  );
}
